import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Upload, Loader2, ScanLine, AlertTriangle, Sparkles } from "lucide-react";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";

interface ScanSectionProps {
  canScanOCR: boolean;
  scanning: boolean;
  onImageSelected: (file: File) => void;
}

const ScanSection = ({ canScanOCR, scanning, onImageSelected }: ScanSectionProps) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { effectiveBetaMode: BETA_MODE } = useEffectiveBetaMode();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onImageSelected(file);
    e.target.value = "";
  };

  return (
    <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <ScanLine className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-heading font-semibold">Scanează Factură</h3>
          <p className="text-sm text-muted-foreground">
            Încarcă o factură pentru completare automată
          </p>
        </div>
      </div>
      {canScanOCR ? (
        <>
          <div className="flex gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="flex-1 h-12"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanning}
                >
                  {scanning ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Încarcă Fișier
                </Button>
              </TooltipTrigger>
              <TooltipContent>Încarcă un PDF sau o imagine cu factura</TooltipContent>
            </Tooltip>
          </div>
          {scanning && (
            <div className="text-center text-sm text-primary animate-pulse">
              Se extrag datele din factură...
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border-2 border-warning/40 bg-warning/10 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-semibold text-foreground">
                {BETA_MODE
                  ? "Scanare automată indisponibilă pe planul Gratuit"
                  : "Funcție Premium — necesită upgrade"}
              </p>
              <p className="text-xs text-muted-foreground">
                {BETA_MODE
                  ? "Încărcarea PDF-urilor și a imaginilor pentru completare automată (OCR) este o funcție premium, disponibilă în planurile Esențial și Profesional. Va fi activată după ieșirea din beta."
                  : "Încărcarea PDF-urilor și a imaginilor pentru completare automată (OCR) este disponibilă doar în planurile Esențial și Profesional. Pe planul Gratuit poți completa factura manual mai jos."}
              </p>
            </div>
          </div>
          {!BETA_MODE && (
            <Button
              size="sm"
              className="w-full"
              onClick={() => navigate("/select-plan")}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Vezi planurile cu OCR
            </Button>
          )}
        </div>
      )}
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ScanSection;
